import React, {createContext, PropsWithChildren} from 'react';
import {getBreakpoint} from '../../../utils/breakpoint';
import {useFeatureServices} from '../../../use-feature-services';
import useGetLang from '../../../hooks/use-get-lang';

export type EventInfoType = {
  pageId: string;
  pageName: string;
  templateType: string;
  brand: string;
};

export type EnvironmentType = {
  applicationId: string;
  featureAppId: string;
  featureAppIdAppVersion: string;
  country: string;
};

export type UserType = {
  browserResolutionBreakpoint: string;
  browserResolutionHeight: string;
  browserResolutionOrientation: string;
  browserResolutionWidth: string;
};

export type PersonalizationType = {
  status: boolean;
};

export type EventsType = {
  eventInfo: EventInfoType;
  environment: EnvironmentType;
  user: UserType;
  personalization: PersonalizationType;
};

const eventDetails: EventsType = {
  eventInfo: {
    pageId: window.location.href,
    pageName: document.title,
    templateType: 'Feature App Template',
    brand: 'passenger',
  },
  environment: {
    applicationId: 'VWCMSD6',
    featureAppId: 'MaintenanceTimeline-D6-CA',
    featureAppIdAppVersion: '1.0',
    country: 'CA',
  },
  user: {
    browserResolutionBreakpoint: getBreakpoint(),
    browserResolutionHeight: window.screen.height.toString(),
    browserResolutionOrientation: window.matchMedia('(orientation: portrait)')
      .matches
      ? 'v'
      : 'h',
    browserResolutionWidth: window.screen.width.toString(),
  },
  personalization: {
    status: false,
  },
};

export enum TrackingEvents {
  FILTER_SELECT_CLICK = 'VWBasic_FilterSelect_Click',
  FILTER_SEARCH_RESULT_LOAD = 'VWBasic_FilterSearchResult_Load',
  FILTER_SEARCH_RESULT_BUTTON_CLICK = 'VWBasic_FilterSearchResultButton_Click',
  FILTER_SEARCH_BUTTON_CLICK = 'VWBasic_FilterSearchButton_Click',
  ACCORDION_OPEN_CLICK = 'VWBasic_AccordionOpen_Click',
  ACCORDION_CLOSE_CLICK = 'VWBasic_AccordionClose_Click',
  BUTTON_CLICK = 'VWBasic_Button_Click',
}

interface TrackingContextInterface {
  init: (
    eventAction: TrackingEvents,
    vehicle: string,
    contentId: string,
    link: any,
    filter: any,
    accordion?: any,
    processValue?: any,
  ) => void;
}

export const TrackingContext = createContext<TrackingContextInterface | null>(
  null,
);

const TrackingContextProvider = ({children} : PropsWithChildren) => {
  const {tracking: trackingManager} = useFeatureServices();
  const language = useGetLang();

  const init = (
    eventAction: TrackingEvents,
    vehicle: string,
    contentId: string,
    link: any,
    filter: any,
    accordion?: any,
    processValue?: any,
  ) => {
    let eventInfo = {...eventDetails.eventInfo, eventAction, contentId};
    let environment = {...eventDetails.environment, language: language};
    const user = {...eventDetails.user};
    const personalization = {...eventDetails.personalization};
    const configuration = {ModelName: vehicle};
    let process = {};

    if (processValue != null) {
      process = {
        StepName: processValue.name,
        StepNumber: processValue.number,
        processStart: Boolean(processValue.processStart),
        processComplete: Boolean(processValue.processComplete),
      };
    }

    let events = {};

    switch (eventAction) {
      case TrackingEvents.FILTER_SEARCH_RESULT_LOAD:
        events = {
          ...events,
          filter,
        };
        break;

      case TrackingEvents.ACCORDION_OPEN_CLICK:
      case TrackingEvents.ACCORDION_CLOSE_CLICK:
        events = {
          ...events,
          link: {
            name: link.name ? link.name : undefined,
            url: link.url ? link.url : window.location.href,
          },
          filter,
          accordion,
        };
        break;

      case TrackingEvents.FILTER_SELECT_CLICK:
      case TrackingEvents.FILTER_SEARCH_RESULT_BUTTON_CLICK:
      case TrackingEvents.FILTER_SEARCH_BUTTON_CLICK:
        events = {
          ...events,
          link: {
            name: link.name ? link.name : undefined,
            url: link.url ? link.url : window.location.href,
          },
          filter,
        };
        break;
    }

    events = {
      ...events,
      eventInfo,
      environment,
      user,
      personalization,
      configuration,
      process,
    };

    trackingManager.track(eventAction, events);
  };

  const TrackingContextContextValue: TrackingContextInterface = {
    init,
  };

  return (
    <TrackingContext.Provider value={TrackingContextContextValue}>
      {children}
    </TrackingContext.Provider>
  );
};

export default TrackingContextProvider;
